<template>
  <div class="trig-series-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$\sum_{k=0}^{n-1} \sin(a + kd) = \frac{\sin(nd/2)}{\sin(d/2)} \sin(a + \frac{(n-1)d}{2})$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="340" viewBox="0 0 600 400" preserveAspectRatio="xMidYMid meet">
           <!-- 坐标轴 -->
           <g transform="translate(100, 300)">
              <line x1="-50" y1="0" x2="450" y2="0" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
              <line x1="0" y1="-250" x2="0" y2="50" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
              
              <!-- 向量叠加链 (Vector Chain) -->
              <g v-for="(v, i) in vectors" :key="'v'+i">
                 <line :x1="v.x1" :y1="-v.y1" :x2="v.x2" :y2="-v.y2" 
                       :stroke="vectorColor(i)" stroke-width="3" />
                 <circle :cx="v.x2" :cy="-v.y2" r="3" :fill="vectorColor(i)" />
              </g>

              <!-- 总和向量 (Sum Vector) -->
              <line x1="0" y1="0" :x2="totalSum.re" :y2="-totalSum.im" 
                    stroke="#f43f5e" stroke-width="4" stroke-dasharray="5,3" marker-end="url(#arrow-sum)" />
              <text :x="totalSum.re + 10" :y="-totalSum.im" fill="#f43f5e" font-size="14" font-weight="900">Sum</text>

              <!-- 各项在虚轴/纵向的投影 (Sine values) -->
              <g v-for="(v, i) in vectors" :key="'p'+i">
                 <line :x1="v.x2" :y1="-v.y2" :x2="-20" :y2="-v.y2" 
                       stroke="rgba(34, 211, 238, 0.2)" stroke-dasharray="2,2" />
                 <rect x="-30" :y="-v.y2 - 2" width="10" height="4" :fill="vectorColor(i)" opacity="0.8" />
              </g>
           </g>

           <defs>
              <marker id="arrow-sum" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#f43f5e" /></marker>
           </defs>
        </svg>

        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>当前项数 n</label>
            <span class="val">{{ n }}</span>
          </div>
          <div class="hud-item orange">
            <label>正弦总和 (Σsin)</label>
            <span class="val">{{ totalSum.im.toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid">
              <div class="s-item">
                 <label>项数 n ({{ n }})</label>
                 <el-slider v-model="n" :min="1" :max="20" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>初相 a ({{ (a / Math.PI * 180).toFixed(0) }}°)</label>
                 <el-slider v-model="a" :min="0" :max="6.28" :step="0.01" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>公差 d ({{ (d / Math.PI * 180).toFixed(0) }}°)</label>
                 <el-slider v-model="d" :min="0.1" :max="1.5" :step="0.01" size="small"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <div class="discovery-content">
              <p>
                <strong>几何意义：</strong>等差数列的正余弦和可以看作一系列等长向量（模为1）在复平面上的首尾相接。
                由于转角 $d$ 恒定，这些向量落在同一个圆弧上。
                <strong>和的本质：</strong>总和即为起点到终点的连线长度。当 $nd$ 接近 $2\pi$ 时，向量链闭合，总和趋于 $0$。
              </p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'TrigSeriesViz',
  data() {
    return {
      n: 8,
      a: 0.5,
      d: 0.4,
      vLen: 40 // Scale for display
    }
  },
  computed: {
    vectors() {
       let currentX = 0;
       let currentY = 0;
       const result = [];
       for (let k = 0; k < this.n; k++) {
          const angle = this.a + k * this.d;
          const nextX = currentX + this.vLen * Math.cos(angle);
          const nextY = currentY + this.vLen * Math.sin(angle);
          result.push({
             x1: currentX, y1: currentY,
             x2: nextX, y2: nextY,
             angle: angle
          });
          currentX = nextX;
          currentY = nextY;
       }
       return result;
    },
    totalSum() {
       const last = this.vectors[this.vectors.length - 1];
       return last ? { re: last.x2, im: last.y2 } : { re: 0, im: 0 };
    }
  },
  methods: {
    vectorColor(i) {
       const h = (i * 30) % 360;
       return `hsl(${h}, 70%, 60%)`;
    }
  }
}
</script>

<style scoped lang="scss">
.trig-series-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 6px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 700; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 800; font-family: monospace; font-size: 0.75rem; } }
.simulation-stage { min-height: 340px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 800; } .val { font-size: 13px; color: #f8fafc; font-weight: 900; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #fff; padding: 12px 18px; border-radius: 12px; border: 1px solid #e2e8f0; .control-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 2px; display: block; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 12px; i { color: #3b82f6; font-size: 1.2rem; } .discovery-content { flex: 1; p { margin: 0; font-size: 11px; color: #334155; line-height: 1.5; strong { color: #0f172a; font-weight: 900; } } } }
</style>
